import { useSyncExternalStore } from "react";
import { useTranslation } from "react-i18next";
import { RefreshCw } from "lucide-react";
import { isSessionReconnecting, subscribeSessionReconnecting } from "@weco/suite-client";

/**
 * Top-bar pill shown while a session renewal is being retried after a
 * transient failure (429 / 5xx / network) — GH-1913. Non-blocking: the user
 * stays signed in and keeps working; it disappears the moment renewal
 * succeeds (or is definitively rejected, which signs out).
 */
export function SessionReconnectingBanner({ compact }: { compact: boolean }) {
  const { t } = useTranslation();
  const reconnecting = useSyncExternalStore(subscribeSessionReconnecting, isSessionReconnecting);
  if (!reconnecting) return null;

  const label = t("shell.sessionReconnecting", { defaultValue: "Reconnecting your session…" });
  return (
    <div
      role="status"
      aria-live="polite"
      data-testid="session-reconnecting"
      className="flex items-center gap-2 rounded-full px-3 shrink-0 bg-amber-500/10 text-amber-700 dark:text-amber-300 ring-1 ring-inset ring-amber-500/30"
      style={{ height: 35 }}
      title={label}
    >
      <RefreshCw size={15} className="animate-spin" aria-hidden />
      <span className={compact ? "sr-only" : "text-[13px] font-medium whitespace-nowrap"}>{label}</span>
    </div>
  );
}
